// Kalenderlogik in Europe/Berlin. Zeitpunkte sind `Date` (UTC), Geschäftstage sind `IsoDate` ('YYYY-MM-DD').
// Nur Intl – läuft unverändert in Browser und Node und hängt nicht von der Zeitzone des Rechners ab.

export const BUSINESS_TZ = 'Europe/Berlin'

/** Kalendertag 'YYYY-MM-DD' (wie Postgres `date`). Lexikografischer Vergleich = zeitlicher Vergleich. */
export type IsoDate = string

export interface ZonedParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
  /** ISO-Wochentag: 1 = Montag … 7 = Sonntag */
  weekday: number
}

const partsFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: BUSINESS_TZ,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  weekday: 'short',
})

const WEEKDAYS: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }

/** Zerlegt einen Zeitpunkt in Berliner Ortszeit. */
export function berlinParts(at: Date): ZonedParts {
  const map: Record<string, string> = {}
  for (const part of partsFormat.formatToParts(at)) map[part.type] = part.value
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
    weekday: WEEKDAYS[map.weekday ?? ''] ?? 0,
  }
}

const pad = (n: number, width = 2): string => String(n).padStart(width, '0')

export const toIsoDate = (year: number, month: number, day: number): IsoDate =>
  `${pad(year, 4)}-${pad(month)}-${pad(day)}`

/** Geschäftstag (Berliner Kalendertag) eines Zeitpunkts. 23:30 UTC am 10.10. ist in Berlin schon der 11.10. */
export function businessDate(at: Date = new Date()): IsoDate {
  const p = berlinParts(at)
  return toIsoDate(p.year, p.month, p.day)
}

export function isIsoDate(value: unknown): value is IsoDate {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const [y, m, d] = splitIso(value)
  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
}

export function assertIsoDate(value: string): IsoDate {
  if (!isIsoDate(value)) throw new RangeError(`Kein gültiges Datum (JJJJ-MM-TT): ${value}`)
  return value
}

function splitIso(date: IsoDate): [number, number, number] {
  return [Number(date.slice(0, 4)), Number(date.slice(5, 7)), Number(date.slice(8, 10))]
}

const utcMs = (date: IsoDate): number => {
  const [y, m, d] = splitIso(assertIsoDate(date))
  return Date.UTC(y, m - 1, d)
}

const fromUtcMs = (ms: number): IsoDate => {
  const d = new Date(ms)
  return toIsoDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())
}

const DAY_MS = 86_400_000

export const addDays = (date: IsoDate, days: number): IsoDate => fromUtcMs(utcMs(date) + days * DAY_MS)

/** Monate addieren; der Tag wird auf das Monatsende begrenzt (31.01. + 1 Monat = 28./29.02.). */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const [y, m, d] = splitIso(assertIsoDate(date))
  const first = new Date(Date.UTC(y, m - 1 + months, 1))
  const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate()
  return toIsoDate(first.getUTCFullYear(), first.getUTCMonth() + 1, Math.min(d, lastDay))
}

/** Ganze Kalendertage von `from` bis `to` (positiv, wenn `to` später liegt). */
export const diffDays = (from: IsoDate, to: IsoDate): number => Math.round((utcMs(to) - utcMs(from)) / DAY_MS)

/** ISO-Wochentag: 1 = Montag … 7 = Sonntag. */
export const weekdayOf = (date: IsoDate): number => new Date(utcMs(date)).getUTCDay() || 7

export const startOfWeek = (date: IsoDate): IsoDate => addDays(date, 1 - weekdayOf(date))
export const startOfMonth = (date: IsoDate): IsoDate => `${assertIsoDate(date).slice(0, 7)}-01`
export const endOfMonth = (date: IsoDate): IsoDate => addDays(addMonths(startOfMonth(date), 1), -1)
export const startOfYear = (date: IsoDate): IsoDate => `${assertIsoDate(date).slice(0, 4)}-01-01`

/** Kalenderwoche nach ISO 8601 (Wochen beginnen am Montag; KW 1 enthält den ersten Donnerstag). */
export function isoWeek(date: IsoDate): { year: number; week: number } {
  const thursday = addDays(date, 4 - weekdayOf(date))
  const year = Number(thursday.slice(0, 4))
  return { year, week: Math.floor(diffDays(`${pad(year, 4)}-01-01`, thursday) / 7) + 1 }
}

/** Abstand Berlin − UTC in Minuten zu einem Zeitpunkt (+60 Winter, +120 Sommer). */
export function berlinOffsetMinutes(at: Date): number {
  const p = berlinParts(at)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60_000)
}

/**
 * Berliner Ortszeit → Zeitpunkt. `time` als 'HH:mm' oder 'HH:mm:ss'.
 * Nicht existierende Zeiten (Sommerzeit-Lücke, 02:30 am Umstellungstag) rücken um die Lücke vor;
 * doppelte Zeiten (Rückstellung) ergeben das zweite Vorkommen (Winterzeit).
 */
export function berlinTimeToDate(date: IsoDate, time = '00:00'): Date {
  const [y, m, d] = splitIso(assertIsoDate(date))
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time)
  if (!match) throw new RangeError(`Uhrzeit als HH:mm erwartet: ${time}`)
  const guess = Date.UTC(y, m - 1, d, Number(match[1]), Number(match[2]), Number(match[3] ?? 0))
  const first = berlinOffsetMinutes(new Date(guess))
  let result = guess - first * 60_000
  const second = berlinOffsetMinutes(new Date(result))
  if (second !== first) result = guess - second * 60_000
  return new Date(result)
}

/** Beginn des Geschäftstags (00:00 Berlin) als Zeitpunkt. */
export const startOfBusinessDay = (date: IsoDate): Date => berlinTimeToDate(date, '00:00')

/** Ende des Geschäftstags = Beginn des Folgetags (exklusive Obergrenze für `at >= von and at < bis`). */
export const endOfBusinessDay = (date: IsoDate): Date => berlinTimeToDate(addDays(date, 1), '00:00')

/** Halboffenes Zeitfenster [from, to) für einen Datumsbereich einschließlich beider Tage. */
export const businessRange = (from: IsoDate, to: IsoDate): { from: Date; to: Date } => ({
  from: startOfBusinessDay(from),
  to: endOfBusinessDay(to),
})

export const minIsoDate = (a: IsoDate, b: IsoDate): IsoDate => (a <= b ? a : b)
export const maxIsoDate = (a: IsoDate, b: IsoDate): IsoDate => (a >= b ? a : b)
